import React from 'react';
import { motion } from 'motion/react';
import { Users, Target, ShieldCheck, Zap } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const MarketingTeams = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Social Media Tool for Marketing Teams - PlataPost"
        description="Empower your marketing team with PlataPost's AI tools. Streamline campaign planning, maintain brand consistency, and collaborate efficiently."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Users className="w-3.5 h-3.5" />
              <span>Team Collaboration</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI for Modern <br /> <span className="text-indigo-500">Marketing Teams.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Efficiency meets brand consistency. PlataPost helps corporate marketing teams align their social strategy across departments and regions with ease.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
               Empower My Team
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 relative overflow-hidden border border-white/5 shadow-2xl shadow-indigo-600/5 group">
             <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
             <div className="space-y-6 relative z-10 transition-transform duration-700 group-hover:scale-[1.03]">
                <div className="p-6 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-between shadow-xl">
                   <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white/10 rounded-full animate-pulse shadow-inner" />
                      <div className="h-3 w-32 bg-white/10 rounded-full" />
                   </div>
                   <div className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-500/20">Approved</div>
                </div>
                <div className="p-6 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-between shadow-xl">
                   <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white/10 rounded-full shadow-inner" />
                      <div className="h-3 w-40 bg-white/10 rounded-full" />
                   </div>
                   <div className="px-3 py-1.5 bg-amber-500/10 text-amber-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-amber-500/20">Pending Review</div>
                </div>
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Streamlined Collaboration</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Foster teamwork and maintain brand integrity across all channels.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
             {[
               { icon: Target, title: "Campaign Alignment", desc: "Ensure your social content perfectly matches your quarterly marketing goals." },
               { icon: ShieldCheck, title: "Brand Governance", desc: "Centralized brand voice settings prevent 'off-brand' mistakes." },
               { icon: Zap, title: "Faster Turnaround", desc: "Go from campaign concept to scheduled social posts in minutes, not days." }
             ].map((feature, i) => (
                <div key={i} className="p-12 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                   <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-10 text-indigo-400 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-indigo-600/5">
                      <feature.icon className="w-10 h-10" />
                   </div>
                   <h3 className="text-3xl font-black text-white mb-6 tracking-tight">{feature.title}</h3>
                   <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
                </div>
             ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
           <div className="absolute inset-0 bg-indigo-600/10 blur-[150px] pointer-events-none" />
           <h2 className="text-4xl lg:text-6xl font-black text-white mb-10 tracking-tighter leading-tight">Ready to streamline your <span className="text-indigo-400">workflow?</span></h2>
           <Link to="/dashboard" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Get Started for Free
           </Link>
        </div>
      </div>
    </div>
  );
};

export default MarketingTeams;
